import { useEffect, useState } from 'react';
import { Alert, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useAuth } from '../../hooks/useAuth';

/**
 * ConfirmationBanner — thin reassurance banner shown after a 2FA-verified
 * login (or first-post-flag-flip login for SSO users).
 *
 * Amendment to the approved plan (per 4b authorization)
 * -----------------------------------------------------
 * - Cap at 3 total displays per user per browser, tracked in localStorage.
 * - Counter increments on MOUNT (write timing), not on dismiss, so a user
 *   who never notices the banner still consumes one display and the cap
 *   still applies.
 * - If the stored userSub does not match the current user, treat as a
 *   fresh user: reset counter and start at 1 on this mount.
 * - localStorage disabled/cleared: fail silently — banner re-shows on
 *   the next login. Minor UX annoyance, not a security concern.
 *
 * Visibility is driven entirely by AuthProvider.twofaAckShouldShow so the
 * component itself stays declarative. useAuth owns the eligibility rule
 * (read in finalizeTwoFaAuth / setUser paths) and exposes the boolean.
 */

export default function ConfirmationBanner() {
  const { twofaAckShouldShow, markTwofaAckShown } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (twofaAckShouldShow && !dismissed) {
      // Write timing: increment displayCount on mount. This deliberately
      // runs before the user has a chance to interact — we want the cap
      // to hold even if they never look.
      markTwofaAckShown();
    }
  }, [twofaAckShouldShow, dismissed, markTwofaAckShown]);

  if (!twofaAckShouldShow || dismissed) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1500,
        width: 'min(92vw, 520px)',
      }}
    >
      <Alert
        icon={<ShieldOutlinedIcon sx={{ color: '#2D7D46' }} />}
        severity="success"
        sx={{
          bgcolor: '#F0FDF4',
          border: '1px solid rgba(45, 125, 70, 0.20)',
          color: '#1F2937',
          borderRadius: 2,
          alignItems: 'center',
        }}
        action={
          <IconButton
            size="small"
            aria-label="dismiss confirmation"
            onClick={() => setDismissed(true)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        Two-factor verification complete. Your session is protected by an
        extra layer of authentication.
      </Alert>
    </Box>
  );
}
